import DownloadButton from "../DownloadButton";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/POInvoiceLayout.css";
import "../../styles/components/transactionPrintLayouts/InProcessInspectionInvoiceLayout.css";

export const InProcessInspectionInvoiceLayout = ({
  headDetails,
  itemDetails,
  invoiceTableData,
  headFields,
}) => {
  const organizationData =
    JSON.parse(localStorage.getItem("organisation")) || {};

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}-${month}-${d.getFullYear()}`;
  };

  const formatRefValue = (value) =>
    String(value || "")
      .replace(/\s*\/\s*/g, "/")
      .trim();

  const rows = Array.isArray(itemDetails) ? itemDetails : [];
  const headingFields = Array.isArray(invoiceTableData?.headings)
    ? invoiceTableData.headings
    : [];
  const transactionHeadFields = Array.isArray(headFields) ? headFields : [];

  const getHeadField = (details, ...keys) => {
    for (const key of keys) {
      const value = details?.[key];
      if (value !== null && value !== undefined && value !== "") return value;
    }
    return "";
  };

  const getField = (row, ...keys) => {
    for (const key of keys) {
      const value = row?.[key];
      if (value !== null && value !== undefined && value !== "") return value;
    }
    return "";
  };

  const normalizeFieldName = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const findHeadingFieldName = (...possibleNames) => {
    const normalizedCandidates = possibleNames.map((name) =>
      normalizeFieldName(name),
    );
    const matched = headingFields.find((field) => {
      const normalizedFieldName = normalizeFieldName(field?.field_name);
      const normalizedDisplayName = normalizeFieldName(field?.display_name);
      return normalizedCandidates.some(
        (candidate) =>
          candidate &&
          (candidate === normalizedFieldName ||
            candidate === normalizedDisplayName),
      );
    });
    return matched?.field_name;
  };

  const serialNumberField = findHeadingFieldName(
    "sl_no",
    "si_no",
    "serial_no",
    "serial_number",
    "s_no",
  );
  const parameterField = findHeadingFieldName("parameter", "characteristics");
  const specificationField = findHeadingFieldName(
    "specification",
    "parameter_value",
    "spec",
  );
  const toleranceField = findHeadingFieldName("tolerance", "tolerence");
  const instrumentsField = findHeadingFieldName("instruments", "instrument");
  const fpaField = findHeadingFieldName("fpa", "ipa", "status");
  const inspectionNumberField = transactionHeadFields.find(
    (field) => normalizeFieldName(field?.field_name) === "tranno",
  );
  const inspectionNumberLabel =
    inspectionNumberField?.display_name || inspectionNumberField?.title;

  const observationReadingNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  const normalizeText = (value) => {
    if (value === null || value === undefined) return "";
    const text = String(value).trim();
    return text === "." ? "" : text;
  };

  const headRemarks = normalizeText(
    getHeadField(headDetails, "remark", "remarks", "narration"),
  );
  const itemRemarks = rows
    .map((row) => normalizeText(getField(row, "remarks", "remark")))
    .filter((value) => value)
    .join(", ");
  const remarksText = itemRemarks || headRemarks;

  const getReadingValue = (row, readingNumber) => {
    const resolvedReadingField = findHeadingFieldName(
      String(readingNumber),
      `obv_${readingNumber}`,
      `obs_${readingNumber}`,
      `value_${readingNumber}`,
      `reading_${readingNumber}`,
      `observation_${readingNumber}`,
    );

    return getField(
      row,
      resolvedReadingField,
      `obv_${readingNumber}`,
      `obs_${readingNumber}`,
      String(readingNumber),
      `value_${readingNumber}`,
      `reading_${readingNumber}`,
    );
  };

  const MetaCell = ({
    label,
    value,
    noRightBorder = false,
    inline = false,
  }) => (
    <div
      className={`report-header-section inprocess-meta-cell ${
        inline ? "inprocess-meta-cell-inline" : ""
      } ${noRightBorder ? "inprocess-no-right-border" : ""}`}
    >
      <h4 className="invoice-section-title inprocess-meta-title">{label}</h4>
      <span className="invoice-section-text report-header-text">
        {value || ""}
      </span>
    </div>
  );

  return (
    <div className="invoice-container inprocess-inspection-page-container">
      <div className="inprocess-header-grid">
        <div className="inprocess-header-company-block">
          <div className="invoice-logo-container inprocess-header-logo-container">
            <img
              src={organizationData.logo_url || ProfilePlaceholder}
              alt="Logo"
              className="invoice-logo-content"
            />
          </div>
          <div className="inprocess-header-company-text-wrap">
            <h3 className="invoice-company-name inprocess-company-name">
              {organizationData.name || "N/A"}
            </h3>
            {organizationData.address && (
              <p className="invoice-company-address inprocess-company-address">
                {organizationData.address}
              </p>
            )}
          </div>
        </div>

        <div className="inprocess-header-title-block">
          <h2 className="inprocess-header-title">
            In Process Inspection Report
          </h2>
        </div>

        <div className="inprocess-header-ref-block">
          <div className="inprocess-header-ref-row">
            <strong className="inprocess-header-ref-label">REF. NO.:</strong>
            <span>
              {getHeadField(
                headDetails,
                "ref_no",
                "ref_number",
                "reference_no",
              )
                ? formatRefValue(
                    getHeadField(
                      headDetails,
                      "ref_no",
                      "ref_number",
                      "reference_no",
                    ),
                  )
                : "FP/F/QA/03"}
            </span>
          </div>
          <div className="inprocess-header-ref-row">
            <strong className="inprocess-header-ref-label">REV. NO.:</strong>
            <span>{getHeadField(headDetails, "rev_no", "revision_no")}</span>
          </div>
          <div className="inprocess-header-ref-row">
            <strong className="inprocess-header-ref-label">REV. DATE:</strong>
            <span>
              {getHeadField(headDetails, "rev_date", "revision_date")
                ? formatDate(
                    getHeadField(headDetails, "rev_date", "revision_date"),
                  )
                : ""}
            </span>
          </div>
        </div>
      </div>

      <div className="inprocess-meta-row inprocess-meta-row-4">
        <MetaCell
          label="Date"
          value={formatDate(
            getHeadField(
              headDetails,
              "date",
              "created_at",
              "inspection_date",
              "createdAt",
            ),
          )}
          inline={true}
        />
        <MetaCell
          label="Operator Name"
          value={getHeadField(headDetails, "operator_name", "operator")}
        />
        <MetaCell
          label="Work Order No."
          value={getHeadField(
            headDetails,
            "workorder_no",
            "wo_no",
            "work_order_no",
          )}
        />
        <MetaCell
          label="Description"
          value={getHeadField(headDetails, "narration")}
          noRightBorder={true}
        />
      </div>

      <div className="inprocess-meta-row inprocess-meta-row-2 inprocess-meta-row-inline">
        <MetaCell
          label={inspectionNumberLabel}
          value={getHeadField(headDetails, "tran_no")}
          inline={true}
        />
        <MetaCell
          label="Part No."
          value={getHeadField(
            headDetails,
            "part_no",
            "part_number",
            "item_code",
          )}
          inline={true}
          noRightBorder={true}
        />
      </div>

      <div className="inprocess-meta-row inprocess-meta-row-4">
        <MetaCell
          label="Operation"
          value={getHeadField(headDetails, "operation")}
        />
        <MetaCell
          label="Machine No."
          value={getHeadField(headDetails, "machine_no")}
        />
        <MetaCell label="Shift" value={getHeadField(headDetails, "shift")} />
        <MetaCell
          label="Qty"
          value={getHeadField(
            headDetails,
            "qty",
            "quantity",
            "total_qty",
            "lot_qty",
          )}
          noRightBorder={true}
        />
      </div>

      <div className="invoice-overflow-x-scroll">
        <table className="invoice-table">
          <thead className="invoice-thead">
            <tr>
              <th className="invoice-th inprocess-first-col" rowSpan={2}>
                SL NO
              </th>
              <th className="invoice-th" rowSpan={2}>
                Parameter
              </th>
              <th className="invoice-th" rowSpan={2}>
                Specifications
              </th>
              <th className="invoice-th" rowSpan={2}>
                Tolerance
              </th>
              <th className="invoice-th" rowSpan={2}>
                Instruments
              </th>
              <th className="invoice-th" rowSpan={2}>
                FPA
              </th>
              <th className="invoice-th" colSpan={10}>
                OBSERVATION READING
              </th>
            </tr>
            <tr>
              {observationReadingNumbers.map((n, idx) => (
                <th
                  key={n}
                  className={`invoice-th ${
                    idx === observationReadingNumbers.length - 1
                      ? "inprocess-no-right-border"
                      : ""
                  }`}
                >
                  {n}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                <td className="invoice-td inprocess-first-col">
                  {getField(
                    row,
                    serialNumberField,
                    "sl_no",
                    "si_no",
                    "serial_no",
                    "serial_number",
                    "s_no",
                  ) || idx + 1}
                </td>
                <td className="invoice-td">
                  {getField(
                    row,
                    parameterField,
                    "parameter",
                    "characteristics",
                  )}
                </td>
                <td className="invoice-td">
                  {getField(
                    row,
                    specificationField,
                    "specification",
                    "parameter_value",
                    "spec",
                  )}
                </td>
                <td className="invoice-td">
                  {getField(row, toleranceField, "tolerance", "tolerence")}
                </td>
                <td className="invoice-td">
                  {getField(row, instrumentsField, "instruments", "instrument")}
                </td>
                <td className="invoice-td">
                  {getField(row, fpaField, "fpa", "ipa", "status")}
                </td>
                {observationReadingNumbers.map((colNum, readingIndex) => (
                  <td
                    key={colNum}
                    className={`invoice-td ${
                      readingIndex === observationReadingNumbers.length - 1
                        ? "inprocess-no-right-border"
                        : ""
                    }`}
                  >
                    {getReadingValue(row, colNum)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="inprocess-footer-grid">
        <div className="inprocess-footer-signature-box">
          <strong>QC Incharge Signature:</strong>{" "}
          {getHeadField(
            headDetails,
            "inspected_by",
            "checked_by",
            "qc_incharge",
          )}
        </div>
        <div className="inprocess-footer-signature-box">
          <strong>Shift Incharge:</strong>{" "}
          {getHeadField(
            headDetails,
            "shift_incharge",
            "approved_by",
            "supervisor",
          )}
        </div>
        <div className="inprocess-footer-note-box">
          <strong>Note: </strong>
          1. Every Shift: 1st QP Inspection 5/Person. 2. Color Intervals Every
          ONE Hour for Inprocess Inspection. 3. 4th First Piece Approved.
        </div>
      </div>

      <div className="inprocess-remarks-row">
        <strong>REMARKS:</strong> {remarksText}
      </div>

      <p className="invoice-system-generated-note">
        This is a system-generated document and does not require a signature.
      </p>

      <div className="invoice-buttons inprocess-download-buttons">
        <DownloadButton
          tableData={invoiceTableData}
          fileName="in-process-inspection-report"
          downloadFormats={["excel", "pdf", "full-page-pdf"]}
          fullPageDownload={true}
          fullPageSelector=".inprocess-inspection-page-container"
        />
      </div>
    </div>
  );
};
